/**
 * 图片特效工作画布尺寸计算，供 /design 等共用编辑器的 WASM 渲染使用
 * 保留图片比例，通过等比降采样和透明补边限制 SDF 中间纹理，并提供坐标与参数补偿
 */

// 当前 WASM 会将 SDF 短边放大到 512；长边预算 8192 对应最多 4M 像素，给中间缓冲留出内存。
const SDF_SHORT_EDGE = 512;
const SDF_LONG_EDGE_BUDGET = 8192;
// 同时限制输入纹理面积，避免大正方形图占满 WASM 的多个 RGBA 中间缓冲。
const INPUT_PIXEL_BUDGET = 4 * 1024 * 1024;

/**
 * 计算受设备与中间纹理预算约束的特效工作画布
 * @param width - 原图层逻辑宽度
 * @param height - 原图层逻辑高度
 * @param expansion - 特效外扩倍数
 * @param textureLimit - 当前 WASM 上下文支持的纹理与渲染缓冲尺寸上限
 * @returns 工作画布像素尺寸、等比缩放比例、参数补偿及逻辑坐标偏移
 */
export const getEffectRenderSize = (width: number, height: number, expansion: number, textureLimit: number) => {
    if (![width, height, expansion, textureLimit].every((value) => Number.isFinite(value) && value > 0)) {
        throw new Error('Invalid image effect render size');
    }
    // 投影还会在输入外扩边界之外分配输出纹理，给这一阶段预留一倍边长。
    const limit = Math.floor(Math.min(textureLimit / 2, SDF_LONG_EDGE_BUDGET));
    if (limit < SDF_SHORT_EDGE) throw new Error('Image effect texture limit is too small');
    const scale = Math.min(
        1,
        limit / (Math.max(width, height) * expansion),
        Math.sqrt(INPUT_PIXEL_BUDGET / (width * height * expansion * expansion)),
    );
    const baseWidth = Math.max(1, Math.floor(width * expansion * scale));
    const baseHeight = Math.max(1, Math.floor(height * expansion * scale));
    const minEdge = Math.ceil((Math.max(baseWidth, baseHeight) * SDF_SHORT_EDGE) / limit);
    const canvasWidth = Math.max(baseWidth, minEdge);
    const canvasHeight = Math.max(baseHeight, minEdge);
    const adjusted = scale !== 1 || canvasWidth !== baseWidth || canvasHeight !== baseHeight;
    const virtualWidth = canvasWidth / expansion;
    const virtualHeight = canvasHeight / expansion;
    return {
        canvasWidth,
        canvasHeight,
        scale,
        adjusted,
        virtualWidth,
        virtualHeight,
        offsetX: (virtualWidth - width * scale) / 2,
        offsetY: (virtualHeight - height * scale) / 2,
        paramsScale: adjusted
            ? Math.min(1, (Math.min(width, height) * expansion * scale) / Math.min(canvasWidth, canvasHeight))
            : 1,
    };
};
